import { useEffect, useRef, useState } from 'react';
import type {
  MermaidContent,
  ScriptElement,
} from '../../schema';

/**
 * Mermaid diagram element (INTEGRATION §3.2 / BLUEPRINT §14). The pack carries
 * no image or asset for it — only `content.source` (plain mermaid text). We run
 * mermaid client-side to produce an SVG that fits the element box.
 *
 * mermaid is bundled LOCALLY (the classroom is offline) and lazy-loaded so it's
 * not in the first-paint path — it only loads when a slide actually has a
 * mermaid element. We render ONLY `source`; `diagramType` is an editor hint.
 */

// Minimal surface of the mermaid API we use — avoids an inline dynamic-import
// type annotation (not allowed under this module config).
interface MermaidApi {
  initialize(config: Record<string, unknown>): void;
  render(id: string, source: string): Promise<{ svg: string }>;
}

// Lazy singleton — initialized once on first use.
let mermaidPromise: Promise<MermaidApi> | null = null;
function getMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => {
      const api = m.default as unknown as MermaidApi;
      api.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: 'default',
      });
      return api;
    });
  }
  return mermaidPromise;
}

// mermaid.render() needs a fresh DOM id every call (reusing one throws).
let seq = 0;

export function MermaidElement({ element }: { element: ScriptElement }) {
  const content = element.content as MermaidContent | undefined;
  const source = content?.source ?? '';
  // Authored text size — mermaid bakes font size into the SVG, so it has to be
  // injected as an init directive. Skipped when the author already wrote their
  // own `%%{init ...}%%` so we never emit a duplicate directive.
  const fontSize = (element.style?.fontSize as number | undefined) ?? 16;
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!source.trim()) {
      if (hostRef.current) hostRef.current.innerHTML = '';
      return undefined;
    }
    (async () => {
      try {
        const mermaid = await getMermaid();
        const src = source.includes('%%{init')
          ? source
          : `%%{init: {"themeVariables": {"fontSize": "${fontSize}px"}}}%%\n${source}`;
        // eslint-disable-next-line no-plusplus
        const { svg } = await mermaid.render(`mmd-${seq++}`, src);
        if (cancelled || !hostRef.current) return;
        // svg is sanitized (securityLevel: 'strict').
        hostRef.current.innerHTML = svg;
        // Fit the SVG to the element box without distorting it.
        const node = hostRef.current.querySelector('svg');
        if (node) {
          // Scale to fill the element box so resizing the element actually
          // resizes the diagram; viewBox + preserveAspectRatio keeps it
          // undistorted rather than pinning it at its natural size.
          node.style.maxWidth = '100%';
          node.style.maxHeight = '100%';
          node.style.width = '100%';
          node.style.height = '100%';
        }
        setError(false);
      } catch {
        if (cancelled) return;
        if (hostRef.current) hostRef.current.innerHTML = '';
        setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, fontSize]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        overflow: 'hidden',
      }}
    >
      {error ? (
        <div style={{ fontSize: 13, fontStyle: 'italic', color: '#b23b30' }}>
          Invalid diagram
        </div>
      ) : (
        <div
          ref={hostRef}
          className="mmd-host"
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        />
      )}
      {content?.caption && (
        <div style={{ fontSize: 14, color: '#5a5246', textAlign: 'center' }}>
          {content.caption}
        </div>
      )}
    </div>
  );
}
